import * as React from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FailureSnackbar from '../../../../../../shared/feedback/FailureSnackbar';
import { OrderRow } from '../../../BookingsCalendarTypes';
import { checkCanSplitSelected, checkIfNoProductsAreSelected } from '../state/splitProductUtils';

export type ProductsToSplitOnLabels = {
    canNotSplitOnNoProducts: string;
    canNotSplitAllProducts: string;
};

type ProductsToSplitOnProps = {
    products: OrderRow[];
    onSubmit: (formData: FormData) => void;
    orderId: number;
    formId: string;
    labels: ProductsToSplitOnLabels;
    closeSplitOrderModal: () => void;
    hasMainProduct: boolean;
};

type ProductCheckboxProps = {
    id: string;
    name?: string;
    label: string;
};

function ProductCheckbox({ id, name, label }: ProductCheckboxProps) {
    return (
        <label
            style={{
                display: 'flex',
                alignItems: 'center',
                borderBottom: '1px solid #e0e0e0'
            }}
            htmlFor={id}
        >
            <Typography
                variant="body1"
                sx={{
                    color: 'rgba(0, 0, 0, 0.87)',
                    fontSize: '14px',
                    fontFamily: 'Roboto',
                    fontWeight: '400',
                    lineHeight: '20px',
                    letterSpacing: '0.15px',
                    wordWrap: 'break-word',
                    padding: '16px'
                }}
            >
                {label}
            </Typography>
            <Box sx={{ flex: '1 auto' }} />
            <Checkbox sx={{ paddingRight: '16px' }} id={id} name={name} />
        </label>
    );
}

export function ProductsToSplitOn({
    products,
    closeSplitOrderModal,
    onSubmit,
    orderId,
    formId,
    labels,
    hasMainProduct
}: ProductsToSplitOnProps) {
    let formRef = React.useRef<HTMLFormElement | null>(null);
    let [showNoProductsSnackbar, setShowNoProductsSnackbar] = React.useState<boolean>(false);
    let [showCanNotSplitAllProductsSnackbar, setShowCanNotSplitAllProductsSnackbar] =
        React.useState<boolean>(false);

    return (
        <>
            <form
                ref={formRef}
                id={formId}
                onSubmit={(event) => {
                    event.preventDefault();

                    let formData = new FormData(formRef.current);

                    let areNoProductsChecked = checkIfNoProductsAreSelected(products, formData);
                    if (areNoProductsChecked) {
                        setShowNoProductsSnackbar(true);
                        return;
                    }

                    let isPossibleToSplitSelectedProducts = checkCanSplitSelected(
                        hasMainProduct,
                        products,
                        formData
                    );
                    if (!isPossibleToSplitSelectedProducts) {
                        setShowCanNotSplitAllProductsSnackbar(true);
                        return;
                    }

                    onSubmit(formData);

                    closeSplitOrderModal();
                }}
            >
                <input type="hidden" name="orderId" value={orderId} />
                {products.map((product) => (
                    <ProductCheckbox
                        key={product.productId}
                        id={`product-${product.productId}`}
                        name={`product-${product.productId}`}
                        label={`${product.productOverrideName} (${product.productOriginalName})`}
                    />
                ))}
            </form>
            <FailureSnackbar
                open={showNoProductsSnackbar}
                onClose={() => {
                    setShowNoProductsSnackbar(false);
                }}
                text={labels.canNotSplitOnNoProducts}
            />
            <FailureSnackbar
                open={showCanNotSplitAllProductsSnackbar}
                onClose={() => {
                    setShowCanNotSplitAllProductsSnackbar(false);
                }}
                text={labels.canNotSplitAllProducts}
            />
        </>
    );
}
